import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import authService, { User } from "../services/authService";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // 인증 상태 확인
  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const userData = await authService.verify();
      setUser(userData);
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  // 로그인
  const login = async (username: string, password: string) => {
    try {
      const response = await authService.login({ username, password });

      if (response.success && response.data) {
        setUser(response.data.user);
      } else {
        throw new Error(response.error || "로그인 실패");
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  // 로그아웃
  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  // 컴포넌트 마운트시 인증 확인 (한 번만 실행)
  useEffect(() => {
    if (!isInitialized) {
      checkAuth();
    }
  }, [isInitialized]);

  // 30분마다 토큰 갱신
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        authService.refresh().catch(console.error);
      }, 30 * 60 * 1000); // 30분

      return () => clearInterval(interval);
    }
    return undefined;
  }, [user]);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
